/* eslint-disable no-nested-ternary */
import { CheckCircleOutlined, LoadingOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Flex,
  Input, Tag, Tooltip
} from 'antd';
import React, { useEffect, useState } from 'react';
import {
  Controller, FormProvider, useForm
} from 'react-hook-form';
import { useMutation } from 'react-query';
import { Label, Radio, Typography } from 'tfc-components';

import {
  IBillingInfo
} from '.';

import { checkExistEmailService } from 'services/accounts';
import mapModifiers from 'utils/functions';
import { billingInfoSchema } from 'utils/schemas';

type FormType = IBillingInfo;

interface BillingTypeProps {
  onSubmit: (data: FormType) => void;
  triggerSubmitRef?: React.MutableRefObject<(() => void) | null>;
  initialData?: FormType;
}

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const initialInvoiceEmail = (value: string[] | undefined): string => (value ? value.map((email) => email.trim()).join(',') : '');
const BillingInformation: React.FC<BillingTypeProps> = ({
  onSubmit,
  triggerSubmitRef,
  initialData,
}) => {
  const [inputValue, setInputValue] = useState<string>(
    initialInvoiceEmail(initialData?.invoice?.invoiceEmail)
  );
  const [listOption, setListOption] = useState<string[]>([]);
  const [invoiceType, setInvoiceType] = useState<'business' | 'individual'>(initialData?.invoiceType || 'business');

  const method = useForm<FormType>({
    defaultValues: initialData || {
      fullName: '',
      phone: '',
      email: '',
      invoiceType: 'business',
      invoice: invoiceType === 'business'
        ? {
          companyName: '',
          fullAddress: '',
          invoiceEmail: [],
          taxId: '',
        }
        : {
          fullName: '',
          fullAddress: '',
          invoiceEmail: [],
          nationalId: '',
        },
    },
    resolver: yupResolver(billingInfoSchema),
    mode: 'onChange'
  });

  const { mutate: checkEmailMutate, isLoading: isLoadingCheckEmail } = useMutation(
    ['checkEmail'],
    checkExistEmailService,
    {
      onSuccess: () => {
        method.clearErrors('email');
        method.clearErrors('invoice.taxId');
        if (method.formState.isValid) {
          method.handleSubmit((formData) => {
            onSubmit(formData);
          })();
        }
      },
      onError: (error) => {
        if (Array.isArray(error)) {
          error.forEach((err) => {
            method.setError(err.field === 'taxCode'
              ? 'invoice.taxId' : err.field, {
              message: err.message,
            });
          });
        } else {
          method.setError('email', { message: 'Something went wrong, please try again later' });
        }
      },
    }
  );

  const handleNext = async () => {
    // Validate the entire form
    const isValid = await method.trigger();

    if (!isValid) {
      return;
    }

    const emailValue = method.getValues('email');
    if (!emailValue || !emailRegex.test(emailValue)) {
      method.setError('email', { message: 'Please enter a valid email' });
      return;
    }

    if (method.getValues('invoiceType') === 'business') {
      const taxtaxCode = method.getValues('invoice.taxId').trim();
      if (!taxtaxCode) {
        method.setError('invoice.taxId', { message: 'Please enter a valid tax ID' });
        return;
      }
      const taxCode = method.getValues('invoiceType') === 'business' ? taxtaxCode : undefined;
      checkEmailMutate({ email: emailValue, taxCode });
      return;
    }

    checkEmailMutate({ email: emailValue, taxCode: undefined });
  };

  useEffect(() => {
    if (triggerSubmitRef) {
      // eslint-disable-next-line no-param-reassign
      triggerSubmitRef.current = handleNext;
    }
  }, [triggerSubmitRef]);

  useEffect(() => {
    const newOptions = inputValue
      .split(',')
      .map((opt) => opt.trim())
      .filter((opt) => opt && !listOption.includes(opt));

    setListOption((prev) => [...prev, ...newOptions]);
    setInputValue('');
  }, []);

  const invoiceEmailError = method.formState.errors?.invoice?.invoiceEmail;

  const handleSwitchType = () => {
    const newType = invoiceType === 'business' ? 'individual' : 'business';
    method.setValue('invoiceType', newType);
    setInvoiceType(newType);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === ' ') && inputValue.trim() && !invoiceEmailError) {
      const newOptions = inputValue
        .split(',')
        .map((opt) => opt.trim())
        .filter((opt) => opt && !listOption.includes(opt));

      setListOption((prev) => [...prev, ...newOptions]);
      setInputValue('');
    }
  };

  const handleRemove = (removedOption: string) => {
    const newOptions = listOption.filter((opt) => opt !== removedOption);
    setListOption(newOptions);
    method.setValue('invoice.invoiceEmail', newOptions);
  };

  return (
    <div className="p-checkout_billingInfo">
      <FormProvider {...method}>
        <Typography.Text fontweight="600" extendClasses="p-checkout_titleProduct p-checkout_billingInfo_spacing fs-20x24">
          Buyer Information
        </Typography.Text>
        <div className="p-home_demo_formLayout">
          <div className="p-home_demo_formGroup p-checkout_billingInfo_formGroup">
            <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">
              Full name
              {' '}
              <span className="p-home_demo_quotes">*</span>
            </Typography.Text>
            <Controller
              name="fullName"
              control={method.control}
              rules={{ required: 'Full name is required' }}
              render={({ field }) => (
                <>
                  <Input {...field} className="p-checkout_billingInfo_input p-home_demo_formInput" placeholder="Nguyen Van A" />
                  {method.formState.errors.fullName && (
                    <Typography.Text extendClasses="p-home_demo_error p-checkout_billingInfo_errorInput">{method.formState.errors.fullName.message}</Typography.Text>
                  )}
                </>
              )}
            />
          </div>
          <div className="p-home_demo_formGroup p-checkout_billingInfo_formGroup">
            <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">
              Phone number
              {' '}
              <span className="p-home_demo_quotes">*</span>
            </Typography.Text>
            <Controller
              name="phone"
              control={method.control}
              render={({ field }) => (
                <>
                  <Input
                    {...field}
                    className="p-checkout_billingInfo_input p-home_demo_formInput"
                    placeholder="+84 000 0000 00"
                  />
                  {(method.formState.errors).phone && (
                    <Typography.Text extendClasses="p-home_demo_error p-checkout_billingInfo_errorInput">{(method.formState.errors).phone.message}</Typography.Text>
                  )}
                </>
              )}
            />
          </div>
        </div>
        <div className="p-home_demo_formLayout">
          <div className="p-home_demo_formGroupRow">
            <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">
              Email to create admin account
              {' '}
              <span className="p-home_demo_quotes">*</span>
            </Typography.Text>
            <Controller
              name="email"
              control={method.control}
              render={({ field }) => (
                <>
                  <Input
                    {...field}
                    placeholder="name@example.com"
                    className={mapModifiers('p-checkout_billingInfo_input p-home_demo_formInput', (isLoadingCheckEmail) && 'loading')}
                    disabled={isLoadingCheckEmail}
                    suffix={isLoadingCheckEmail ? <LoadingOutlined /> : emailRegex.test(method.getValues('email'))
                      && method.formState.errors.email ? (<CheckCircleOutlined />) : ''}
                  />
                  {(method.formState.errors as any).email && (
                    <Typography.Text extendClasses="p-home_demo_error p-checkout_billingInfo_errorInput">
                      {(method.formState.errors as any).email.message}
                    </Typography.Text>
                  )}
                </>
              )}
            />
          </div>
        </div>
        <div className="p-checkout_billingType">
          <Typography.Text fontweight="600" extendClasses="p-checkout_titleProduct p-checkout_billingInfo_spacing fs-20x24">Invoice Information</Typography.Text>
          <Label className="p-checkout_billingType_option">
            <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">
              Invoice Recipient Type
              <span className="p-home_demo_quotes">*</span>
            </Typography.Text>
          </Label>
          <div className="p-checkout_billingType_options">
            <Label className="p-checkout_billingType_option">
              <Radio
                size={20}
                checked={invoiceType === 'business'}
                onChange={() => handleSwitchType()}
                name="invoiceType"
              />
              <Typography.Text extendClasses="p-checkout_billingType_optionText">
                Business
              </Typography.Text>
            </Label>
            <Label className="p-checkout_billingType_option">
              <Radio
                size={20}
                name="invoiceType"
                checked={invoiceType === 'individual'}
                onChange={() => handleSwitchType()}
              />
              <Typography.Text extendClasses="p-checkout_billingType_optionText">Individual</Typography.Text>
            </Label>
          </div>
        </div>
        {invoiceType === 'business' ? (
          <>
            <div className="p-home_demo_formLayout">
              <div className="p-home_demo_formGroup p-checkout_billingInfo_formGroup">
                <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">
                  Tax ID
                  {' '}
                  <span className="p-home_demo_quotes">*</span>
                </Typography.Text>
                <Controller
                  name="invoice.taxId"
                  control={method.control}
                  render={({ field }) => (
                    <>
                      <Input
                        {...field}
                        className="p-checkout_billingInfo_input p-home_demo_formInput"
                        onKeyPress={(e) => {
                          if (!/[0-9]/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        placeholder="0312345678"
                      />
                      {(method.formState.errors as any).invoice?.taxId && (
                        <Typography.Text extendClasses="p-home_demo_error p-checkout_billingInfo_errorInput">{(method.formState.errors as any).invoice?.taxId.message}</Typography.Text>
                      )}
                    </>
                  )}
                />
              </div>
              <div className="p-home_demo_formGroup p-checkout_billingInfo_formGroup">
                <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">
                  Company Name
                  {' '}
                  <span className="p-home_demo_quotes">*</span>
                </Typography.Text>
                <Controller
                  name="invoice.companyName"
                  control={method.control}
                  render={({ field }) => (
                    <>
                      <Input {...field} className="p-checkout_billingInfo_input p-home_demo_formInput" placeholder="Company ABC" />
                      {(method.formState.errors as any).invoice?.companyName && (
                        <Typography.Text extendClasses="p-home_demo_error p-checkout_billingInfo_errorInput">{(method.formState.errors as any).invoice?.companyName!.message}</Typography.Text>
                      )}
                    </>
                  )}
                />
              </div>
            </div>

            <div className="p-home_demo_formLayout">
              <div className="p-home_demo_formGroupRow">
                <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">
                  Full Address
                  {' '}
                  <span className="p-home_demo_quotes">*</span>
                  {' '}
                  <Tooltip placement="right" title="Please enter the registered address as provided to the tax authority.">
                    <span><QuestionCircleOutlined /></span>
                  </Tooltip>
                </Typography.Text>
                <Controller
                  name="invoice.fullAddress"
                  control={method.control}
                  render={({ field }) => (
                    <>
                      <Input
                        {...field}
                        placeholder="123 Nguyen Hue St, Ben Nghe Ward, District 1, HCMC"
                        className="p-checkout_billingInfo_input p-home_demo_formInput"
                      />
                      {(method.formState.errors as any).invoice?.fullAddress && (
                        <Typography.Text extendClasses="p-home_demo_error p-checkout_billingInfo_errorInput">
                          {(method.formState.errors as any).invoice?.fullAddress.message}
                        </Typography.Text>
                      )}
                    </>
                  )}
                />
              </div>
            </div>

            <div className="p-home_demo_formLayout">
              <div className="p-home_demo_formGroupRow">
                <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">
                  Invoice Email
                  {' '}
                  <span className="p-home_demo_quotes">*</span>
                </Typography.Text>
                <Controller
                  name="invoice.invoiceEmail"
                  control={method.control}
                  render={({ field }) => (
                    <>
                      <div className="p-addNewPackage_inputWrap">
                        <div className="p-addNewPackage_listTag">
                          {listOption.map((option, index) => (
                            <Flex key={`${index.toString()}tag`}>
                              {index > 0
                                && (
                                  <Typography.Text
                                    extendClasses="p-addNewPackage_mr10"
                                  >
                                    ,
                                  </Typography.Text>
                                )}
                              <Tag
                                key={option}
                                closable
                                onClose={() => handleRemove(option)}
                                className="p-addNewPackage_tag"
                              >
                                {option}
                              </Tag>
                            </Flex>
                          ))}
                        </div>
                        <Input
                          placeholder={listOption.length === 0 ? 'email1@example.com, email2@example.com' : ''}
                          className="modalOption_textInput"
                          onKeyDown={handleKeyDown}
                          onChange={(e) => {
                            const { value } = e.target;
                            if (value !== ' ') {
                              setInputValue(value);
                              field.onChange(listOption.concat(value));
                            }
                          }}
                          value={inputValue}
                          style={{ width: '100%', padding: 8, boxSizing: 'border-box' }}
                        />

                      </div>
                      {invoiceEmailError && (
                        <Typography.Text extendClasses="p-home_demo_error p-checkout_billingInfo_errorInput">
                          {Array.isArray(invoiceEmailError)
                            ? (invoiceEmailError[0] && typeof
                              invoiceEmailError[0] === 'object' && 'message' in invoiceEmailError[0])
                              ? invoiceEmailError[0].message
                              : 'Invalid email'
                            : (invoiceEmailError as any)?.message || 'Invalid email'}
                        </Typography.Text>
                      )}
                    </>

                  )}
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="p-home_demo_formLayout">
              <div className="p-home_demo_formGroup p-checkout_billingInfo_formGroup">
                <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">
                  National ID Number
                  {' '}
                  <span className="p-home_demo_quotes">*</span>
                </Typography.Text>
                <Controller
                  name="invoice.nationalId"
                  control={method.control}
                  render={({ field }) => (
                    <>
                      <Input {...field} className="p-checkout_billingInfo_input p-home_demo_formInput" placeholder="23456789012" />
                      {(method.formState.errors as any).invoice?.nationalId && (
                        <Typography.Text extendClasses="p-home_demo_error p-checkout_billingInfo_errorInput">{(method.formState.errors as any).invoice?.nationalId.message}</Typography.Text>
                      )}
                    </>
                  )}
                />
              </div>
              <div className="p-home_demo_formGroup p-checkout_billingInfo_formGroup">
                <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">
                  Full Name
                  {' '}
                  <span className="p-home_demo_quotes">*</span>
                </Typography.Text>
                <Controller
                  name="invoice.fullName"
                  control={method.control}
                  render={({ field }) => (
                    <>
                      <Input {...field} className="p-checkout_billingInfo_input p-home_demo_formInput" placeholder="Nguyen Van A" />
                      {(method.formState.errors as any).invoice?.fullName && (
                        <Typography.Text extendClasses="p-home_demo_error p-checkout_billingInfo_errorInput">{(method.formState.errors as any).invoice?.fullName.message}</Typography.Text>
                      )}
                    </>
                  )}
                />
              </div>
            </div>

            <div className="p-home_demo_formLayout">
              <div className="p-home_demo_formGroupRow">
                <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">
                  Full Address
                  {' '}
                  <span className="p-home_demo_quotes">*</span>
                </Typography.Text>
                <Controller
                  name="invoice.fullAddress"
                  control={method.control}
                  render={({ field }) => (
                    <>
                      <Input
                        {...field}
                        placeholder="123 Nguyen Hue St, Ben Nghe Ward, District 1, HCMC"
                        className="p-checkout_billingInfo_input p-home_demo_formInput"
                      />
                      {(method.formState.errors as any).invoice?.fullAddress && (
                        <Typography.Text extendClasses="p-home_demo_error p-checkout_billingInfo_errorInput">
                          {(method.formState.errors as any).invoice?.fullAddress.message}
                        </Typography.Text>
                      )}
                    </>
                  )}
                />
              </div>
            </div>

            <div className="p-home_demo_formLayout">
              <div className="p-home_demo_formGroupRow">
                <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">
                  Invoice Email
                  {' '}
                  <span className="p-home_demo_quotes">*</span>
                </Typography.Text>
                <Controller
                  name="invoice.invoiceEmail"
                  control={method.control}
                  render={({ field }) => (
                    <>
                      <div className="p-addNewPackage_inputWrap">
                        <div className="p-addNewPackage_listTag">
                          {listOption.map((option, index) => (
                            <>
                              {index > 0
                                && (
                                  <Typography.Text
                                    extendClasses="p-addNewPackage_mr10"
                                  >
                                    ,
                                  </Typography.Text>
                                )}
                              <Tag
                                key={option}
                                closable
                                onClose={() => handleRemove(option)}
                                className="p-addNewPackage_tag"
                              >
                                {option}
                              </Tag>
                            </>
                          ))}
                        </div>
                        <Input
                          placeholder={listOption.length > 0 ? '' : 'email1@example.com, email2@example.com'}
                          className="modalOption_textInput"
                          onKeyDown={handleKeyDown}
                          onChange={(e) => {
                            const { value } = e.target;
                            if (value !== ' ') {
                              setInputValue(value);
                              field.onChange(listOption.concat(value));
                            }
                          }}
                          value={inputValue}
                          style={{ width: '100%', padding: 8, boxSizing: 'border-box' }}
                        />

                      </div>
                      {invoiceEmailError && (
                        <Typography.Text extendClasses="p-home_demo_error p-checkout_billingInfo_errorInput">
                          {Array.isArray(invoiceEmailError)
                            ? (invoiceEmailError[0] && typeof
                              invoiceEmailError[0] === 'object' && 'message' in invoiceEmailError[0])
                              ? invoiceEmailError[0].message
                              : 'Invalid email'
                            : (invoiceEmailError as any)?.message || 'Invalid email'}
                        </Typography.Text>
                      )}
                    </>

                  )}
                />
              </div>
            </div>
          </>
        )}
        <button type="submit" style={{ display: 'none' }} />
      </FormProvider>
    </div>
  );
};

export default BillingInformation;
